'use client'

import { useEffect } from 'react'

interface DynamicFontLoaderProps {
  fontFamily: string | undefined
}

// List of Google Fonts that need to be loaded
const GOOGLE_FONTS = [
  'Inter',
  'Work Sans',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Raleway',
  'Source Sans Pro',
  'Nunito',
  'Playfair Display',
  'Merriweather',
  'Figtree',
]

// System fonts that don't need loading
const SYSTEM_FONTS = [
  'Arial',
  'Helvetica',
  'Georgia',
  'Times New Roman',
  'Courier New',
]

export function DynamicFontLoader({ fontFamily }: DynamicFontLoaderProps) {
  useEffect(() => {
    if (!fontFamily) return

    // Check if it's a system font (includes fallbacks like "Arial, sans-serif")
    const isSystemFont = SYSTEM_FONTS.some((font) => fontFamily.includes(font))
    if (isSystemFont) return

    // Check if it's a Google Font
    const isGoogleFont = GOOGLE_FONTS.some((font) => fontFamily.includes(font))
    if (!isGoogleFont) return

    // Extract the font name (handle cases like "Work Sans" or "Source Sans Pro")
    const fontName = fontFamily.split(',')[0].trim()

    // Check if the font is already loaded
    const existingLink = document.querySelector(`link[data-font="${fontName}"]`)
    if (existingLink) return

    // Create and append the Google Fonts link
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
      fontName
    )}:wght@300;400;500;600;700;800&display=swap`
    link.setAttribute('data-font', fontName)
    document.head.appendChild(link)
  }, [fontFamily])

  return null
}
